import { useMutation, useQuery } from '@tanstack/react-query';
import { Polls, PollsStatus, QueryParams } from '@/model';
import { PollsConverter } from '@/converters/polls';
import { PollService } from '../instances/polls';
import { useCallback, useMemo } from 'react';
import { GetParamsObject } from '@/model/query-params';

export const useGetPolls = (params?: QueryParams) => {
  const query = useQuery(
    ['GET_POLLS', params],
    async () => {
      const [err, data] = await PollService.getPolls(params);
      if (err) {
        throw err;
      }
      return {
        data: PollsConverter.toDomainFromPollsPaginatedResponse(data),
        meta: data.meta,
      };
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const useGetPollsAll = (limit?: number | null) => {
  return useQuery({
    queryKey: ['GET_POLLS'],
    queryFn: async (): Promise<Polls[]> => {
      let all: Polls[] = [];
      let currentPage = 1;
      let totalPages = 1;
      do {
        const [err, data] = await PollService.getPolls(GetParamsObject({ limit }));
        if (err) throw err;
        all = [...all, ...PollsConverter.toDomainFromPollsPaginatedResponse(data)];
        totalPages = data.meta.pages;
        currentPage++;
      } while (currentPage <= totalPages);
      return all;
    },
  });
};

export const usePutPollsMutation = () => {
  return useMutation({
    mutationKey: ['PUT_POLLS'],
    mutationFn: async ({ id, payload }: any) => {
      const [err, data] = await PollService.putPolls(id, payload);
      if (err) {
        throw err;
      }
      return PollsConverter.toDomainFromPutPollsResponse(data);
    },
  });
};

export const useGetPollsByUUID = (id: string) => {
  const query = useQuery(
    ['GET_POLLS_BY_ID', id],
    async () => {
      const [err, data] = await PollService.getPollsByUUID(id);
      if (err) {
        throw err;
      }
      return PollsConverter.toDomainFromGetPollsByUUIDResponse(data);
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!id,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const useGetPollsById = () => {
  return useMutation({
    mutationKey: ['GET_POLLS_BY_ID'],
    mutationFn: async (uuid: string) => {
      const [err, data] = await PollService.getPollsByUUID(uuid);
      if (err) {
        throw err;
      }
      return PollsConverter.toDomainFromGetPollsByUUIDResponse(data);
    },
  });
};

export const usePostPollsMutation = () => {
  return useMutation({
    mutationKey: ['POST_POLLS'],
    mutationFn: async (payload: any) => {
      const [err, data] = await PollService.postPolls(payload);
      if (err) {
        throw err;
      }
      return PollsConverter.toDomainFromPostPollsResponse(data);
    },
  });
};

export const usePutPollsInteractiveMutation = () => {
  return useMutation({
    mutationKey: ['PUT_POLLS_INTERACTIVE'],
    mutationFn: async ({ id, payload }: any) => {
      const [err] = await PollService.putPollsInteractive(id, payload);
      if (err) {
        throw err;
      }
    },
  });
};

export const useDeletePollsMutation = () => {
  return useMutation({
    mutationKey: ['DELETE_POLLS'],
    mutationFn: async (id: string) => {
      const [err] = await PollService.deletePolls(id);
      if (err) {
        throw err;
      }
      return;
    },
  });
};

export const usePollIfEnable = (isEnable: boolean) => {
  const query = useQuery(
    ['GET_POLLS'],
    async (): Promise<Polls[]> => {
      if (!isEnable) {
        return [];
      }

      let all: Polls[] = [];
      let currentPage = 1;
      let totalPages = 1;
      do {
        const [err, data] = await PollService.getPolls(
          GetParamsObject(
            {
              status: PollsStatus.PUBLISHED,
            },
            undefined,
            900,
          ),
        );
        if (err) throw err;
        all = [...all, ...PollsConverter.toDomainFromPollsPaginatedResponse(data)];
        totalPages = data.meta.pages;
        currentPage++;
      } while (currentPage <= totalPages);
      return all;
    },
    {
      refetchOnWindowFocus: false,
      enabled: isEnable,
      suspense: !isEnable,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};
